import { useCallback, useMemo } from "react";

import { useTranslation } from "next-i18next";
import { Locale, useRouter } from "next/router";

import {
  FormatPriceOptions,
  FormatPricePrice,
  formatPrice,
  resolveLocalePath,
} from "@/common/utils/intl.utils";

/**
 * Hook that returns currency format function.
 *
 * @param options Currency, place and locale. The locale defaults to the
 *   router's, so a component cannot forget it.
 */
export const useFormatPrice = (options?: FormatPriceOptions) => {
  const { locale: routerLocale } = useRouter();
  const locale = options?.locale ?? routerLocale;
  const currency = options?.currency;
  const place = options?.place;

  const format = useCallback(
    (price?: FormatPricePrice) =>
      formatPrice(price, { locale, currency, place }),
    [locale, currency, place]
  );

  return { formatPrice: format };
};

type FormatMinMaxOptions = {
  signGenerator?: (value: number) => string;
};

/**
 * Hook that returns min/max formatted string (price, dimensions, ...)
 * @param options
 */
export const useFormatMinMax = (options?: FormatMinMaxOptions) => {
  const { t } = useTranslation();

  const generateSign = useCallback(
    (value: number, signGenerator?: (value: number) => string) => {
      if (Number.isNaN(value)) {
        return "";
      }

      if (signGenerator) {
        return signGenerator(value);
      }

      return value.toString();
    },
    []
  );

  const format = useCallback(
    (min?: string | number | null, max?: string | number | null) => {
      const minAsNumber = Number(min);
      const minFormatted = generateSign(minAsNumber, options?.signGenerator);

      const maxAsNumber = Number(max);
      const maxFormatted = generateSign(maxAsNumber, options?.signGenerator);

      if (min && max) {
        return `${minFormatted} - ${maxFormatted}`;
      }

      if (min && !max) {
        return `${t("common:common-from")} ${minFormatted}`;
      }

      if (!min && max) {
        return `${t("common:common-to")} ${maxFormatted}`;
      }

      return "";
    },
    [generateSign, options?.signGenerator, t]
  );

  return { format };
};

// ponytail: default catches pages that pass the provider value straight from
// getServerSideProps props that can be absent (e.g. the PDP 304 early return).
export const useLocaleLinks = (urls: Partial<Record<Locale, string>> = {}) => {
  const router = useRouter();

  const links = useMemo(
    () =>
      router.locales?.map(locale => ({
        locale,
        ...resolveLocalePath(locale, urls, router.asPath),
      })) ?? [],
    [router.locales, router.asPath, urls]
  );

  return { links };
};
